import {
  createRouter,
  createWebHistory,
  NavigationGuardNext,
  RouteLocationNormalized,
  RouteRecordRaw,
} from 'vue-router';
import { shouldNotSignedInRoutes, publicRoutes } from '@/config/auth';
import { useAppStore } from '@/store/app';
import { useAuthStore } from '@/store/auth';
import {
  clearIntendedDestination,
  loadIntendedDestination,
  storeIntendedDestination,
  storeLocalInvitationCode,
} from '@/utils/local_storage';

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    redirect: { name: 'Login' },
  },
  {
    path: '/app',
    component: () => import('@/views/layouts/Main.vue'),
    children: [
      {
        path: '/',
        name: 'Home',
        redirect: { name: 'Reports' },
      },
      {
        path: '/profile',
        name: 'Profile',
        component: () => import('@/views/profile/Profile.vue'),
      },
      {
        path: '/subscription',
        name: 'Subscription',
        component: () => import('@/views/subscription/Subscription.vue'),
      },
      {
        path: '/api-key',
        name: 'APIKey',
        component: () => import('@/views/api_key/APIKey.vue'),
      },
      {
        path: '/contact-sales',
        name: 'ContactSales',
        component: () => import('@/views/subscription/ContactSales.vue'),
      },
      {
        path: '/history',
        name: 'History',
        component: () => import('@/views/history/History.vue'),
      },
      // Report routes
      {
        path: '/reports',
        name: 'Reports',
        component: () => import('@/views/videos/Index.vue'),
      },
      {
        path: '/reports/:id',
        name: 'ReportDetail',
        component: () => import('@/views/videos/Show.vue'),
      },
    ],
  },
  {
    path: '/',
    component: () => import('@/views/layouts/Auth.vue'),
    children: [
      {
        path: '/user-profile',
        name: 'UserProfile',
        component: () => import('@/views/profile/UserProfile.vue'),
      },
      {
        path: '/user-team',
        name: 'UserTeam',
        component: () => import('@/views/profile/UserTeam.vue'),
      },
      {
        path: '/login',
        name: 'Login',
        component: () => import('@/views/login/Login.vue'),
      },
      {
        path: '/signup',
        name: 'Signup',
        component: () => import('@/views/signup/Signup.vue'),
        beforeEnter: (to) => {
          // Store the invitation code if present in signup URL
          const code = to.query.code;
          if (typeof code === 'string') {
            storeLocalInvitationCode(code);
          }
        },
        meta: {
          layout: 'Blank',
        },
      },
      {
        path: '/forgot-password',
        name: 'ForgotPassword',
        component: () => import('@/views/forgot_password/ForgotPassword.vue'),
        meta: {
          layout: 'Blank',
        },
      },
      {
        path: '/reset-password',
        name: 'ResetPassword',
        component: () => import('@/views/reset_password/ResetPassword.vue'),
        meta: {
          layout: 'Blank',
        },
      },
    ],
  },
  {
    path: '/privacy-policy',
    name: 'PrivacyPolicy',
    component: () => import('@/views/privacy_policy/PrivacyPolicyJp.vue'),
  },
  {
    path: '/privacy-policy/en',
    name: 'PrivacyPolicyEn',
    component: () => import('@/views/privacy_policy/PrivacyPolicyEn.vue'),
  },
  {
    path: '/terms-of-use',
    name: 'TermsOfUse',
    component: () => import('@/views/terms_of_use/TermsOfUseJp.vue'),
  },
  {
    path: '/terms-of-use/en',
    name: 'TermsOfUseEn',
    component: () => import('@/views/terms_of_use/TermsOfUseEn.vue'),
  },
  {
    path: '/join',
    name: 'Join',
    redirect: (to) => {
      // Store the invitation code before redirecting
      const code = to.query.code;
      if (typeof code === 'string') {
        storeLocalInvitationCode(code);
      }
      return { name: 'Home' };
    },
  },
  {
    path: '/subscribe-now',
    name: 'SubscribeNow',
    redirect: () => {
      // Store intended destination for subscription page
      storeIntendedDestination('Subscription');
      return { name: 'Home', query: { autoCheckout: 'true' } };
    },
  },
  {
    path: '/:catchAll(.*)',
    redirect: { name: 'Login' },
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior() {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  },
});

router.beforeEach((to, from, next) => {
  if (publicRoutes.includes(to.name as string)) {
    return next();
  }
  const appStore = useAppStore();
  const authStore = useAuthStore();

  // for open application from invitation link
  // {DASHBOARD_URL}/join?code={invitation_code} for existing users
  // {DASHBOARD_URL}/signup?code={invitation_code} for new users
  if (
    to.redirectedFrom?.path.includes('/join') ||
    to.path.includes('/join') ||
    to.redirectedFrom?.path.includes('/signup') ||
    to.path.includes('/signup')
  ) {
    const code = to.query.code;
    if (typeof code === 'string') {
      storeLocalInvitationCode(code);
    }
  }
  if (appStore.isFirstRun) {
    appStore.setFirstRun(false);
    restoreLocalUser(to, from, next);
  } else {
    if (!authStore.isSignedIn) {
      return handleTokenInvalid(to, next);
    }

    if (shouldNotSignedInRoutes.includes(to.name as string)) {
      next({ name: 'Home' });
    } else if (!authStore.hasUserProfileAndTeam && to.name !== 'UserProfile' && to.name !== 'UserTeam') {
      // Signed in but hasn't finished signup — keep them in the setup flow
      if (authStore.hasOnlyUserProfile) {
        next({ name: 'UserTeam' });
      } else {
        next({ name: 'UserProfile' });
      }
    } else {
      next();
    }
  }
});

const restoreLocalUser = (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
): void => {
  const authStore = useAuthStore();
  authStore
    .restoreLocalUser()
    .then(() => {
      authStore
        .verifyUserToken()
        .then(() => {
          if (!authStore.isSignedIn) {
            handleTokenInvalid(to, next);
          } else if (authStore.hasUserProfileAndTeam) {
            // Check for intended destination (e.g., from deeplink)
            const intendedDestination = loadIntendedDestination();
            if (intendedDestination) {
              clearIntendedDestination();
              // Preserve query params (like autoCheckout) from current route
              if (shouldNotSignedInRoutes.includes(to.name as string)) {
                next({ name: intendedDestination, query: to.query });
              } else {
                next({ name: intendedDestination, query: to.query });
              }
            } else if (shouldNotSignedInRoutes.includes(to.name as string)) {
              next({ name: 'Home' });
            } else {
              next();
            }
          } else if (authStore.hasOnlyUserProfile) {
            next({ name: 'UserTeam' });
          } else {
            next({ name: 'UserProfile' });
          }
        })
        .catch(() => handleTokenInvalid(to, next));
    })
    .catch(() => handleTokenInvalid(to, next));
};

const handleTokenInvalid = (to: RouteLocationNormalized, next: NavigationGuardNext) => {
  setTimeout(() => {
    if (!shouldNotSignedInRoutes.includes(to.name as string)) {
      next({ name: 'Login' });
    } else {
      next();
    }
  }, 100);
};

export default router;
